import React, { FC, useEffect, useState } from 'react';
import { Box, TextField, Autocomplete, LinearProgress } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { User } from 'models/user';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import {
    getAllClinicNamesConnectedWithLabTemplate,
    getClinicTemplate,
} from 'apollo/templates/organization/query.graphql';
import { Organization } from 'models/organization';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import LabAddDentistButton from 'components/users/shared/lab-add-dentist-button/lab-add-dentist-button.component';

type AddDentistToClinicDialogProps = {
    open: boolean;
    onDialogClose: () => void;
    onDentistCreation: (dentist: User, clinic: Organization) => void;
};
const AddDentistToClinicDialog: FC<AddDentistToClinicDialogProps> = ({
    open,
    onDialogClose,
    onDentistCreation,
}: AddDentistToClinicDialogProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [clinics, setClinics] = useState<Organization[]>([]);
    const [clinic, setClinic] = useState<Organization>();
    const [showAddConnectedDentistButton, setShowAddConnectedDentistButton] = useState<boolean>(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [getConnectedClinicNames, { loading: isClinicsLoading }] = useLazyQuery(
        getAllClinicNamesConnectedWithLabTemplate,
        {
            fetchPolicy: 'no-cache',
            variables: {
                filters: {
                    showParents: true,
                    invoiceEnabled: false,
                    isCollective: false,
                    includeCollectiveDentist: false,
                },
            },
            onCompleted: ({ clinicsForLab }) => {
                setClinics(clinicsForLab);
            },
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const [getClinic] = useLazyQuery(getClinicTemplate, {
        fetchPolicy: 'network-only',
        onError: (err) => {
            console.error(err, 'err');
            showBoundary(err);
        },
        onCompleted: async ({ clinic }) => {
            setClinic(clinic);
        },
    });

    useEffect(() => {
        const fetchData = async () => {
            await getConnectedClinicNames();
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (clinic) {
            let showAddConnectedDentistButton = customerSettings.getCustomerSetting(
                clinic?.customerSettings || [],
                CONSTANTS.SETTINGS_NAMES.ADD_CONNECTED_DENTIST,
            );
            // If the clinic is a child clinic, check the parent clinic for the customer setting
            if (clinic?.parentClinicId) {
                showAddConnectedDentistButton = customerSettings.getCustomerSetting(
                    clinic?.parentClinic?.customerSettings || [],
                    CONSTANTS.SETTINGS_NAMES.ADD_CONNECTED_DENTIST,
                );
            }
            setShowAddConnectedDentistButton(showAddConnectedDentistButton);
        }
    }, [clinic]);

    const handleClinicChange = async (selectedClinic: Organization) => {
        await getClinic({
            variables: {
                id: selectedClinic._id,
            },
        });
    };

    const sendDataToParent = (dentist: User, clinic: Organization) => {
        onDentistCreation(dentist, clinic);
        onDialogClose();
    };

    const handleDentistCreation = (dentist: User) => {
        if (clinic) {
            sendDataToParent(dentist, clinic);
        }
    };

    const handleUserLinked = (user: User) => {
        if (clinic) {
            sendDataToParent(user, clinic);
        }
    };

    return (
        <DHDialog title={translate('orders:ADD_DENTIST')} maxWidth="sm" fullWidth open={open} onClose={onDialogClose}>
            <Box mt={1} mb={3}>
                <Autocomplete
                    id="clinic-dropdown"
                    options={clinics}
                    disableClearable
                    value={clinic as Organization}
                    getOptionLabel={(option: Organization) => option?.name || ''}
                    renderOption={(props, option: Organization) => (
                        <li {...{ ...props, key: option?._id }}>{option?.name || ''}</li>
                    )}
                    isOptionEqualToValue={(option: Organization, value: Organization) => option?._id === value?._id}
                    onChange={(_: React.SyntheticEvent, selectedClinic: Organization) =>
                        handleClinicChange(selectedClinic)
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={translate('common:CLINIC')}
                            required
                            margin="dense"
                            fullWidth={true}
                        />
                    )}
                    loading={isClinicsLoading}
                />
                {isClinicsLoading ? <LinearProgress /> : undefined}

                {clinic?._id ? (
                    <LabAddDentistButton
                        clinicId={clinic?.parentClinicId || clinic?._id}
                        isWhiteLabel={clinic?.isWhiteLabeled || false}
                        showAddConnectedDentistButton={showAddConnectedDentistButton}
                        labClientCustomerSettings={clinic?.customerSettings}
                        onDentistCreation={handleDentistCreation}
                        onUserLinked={handleUserLinked}
                        variant="contained"
                        fullWidth
                        // A child clinic was picked above, so the dentist is created on its
                        // parent - offer that child as the lab's default clinic straight away.
                        initialLabDefaultClinicId={clinic?.parentClinicId ? clinic._id : undefined}
                    />
                ) : undefined}
            </Box>
        </DHDialog>
    );
};

export default AddDentistToClinicDialog;
